import React, { useEffect, useState } from "react";
import { ArrowRight, Snowflake, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../hooks/useResponsive";
import { useScrollAnimation, useParallax } from "../hooks/useAnimations";
import { useContent } from "../cms";
import InteractiveCard from "./InteractiveCard";
import { PrimaryButton, OutlineButton } from './ui/StandardButton';
import HeroHeading from "./ui/HeroHeading";
import { colors } from "../styles/colors";

// Default media paths (fallbacks)
const DEFAULT_VIDEO_URL = "/nya_filmer/coldexperience-top-1.mp4";
const DEFAULT_POSTER_URL = "/images/Nya_bilder/IMG_6698.webp";

const Hero = () => {
  const { t, i18n } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const { getMedia } = useContent();
  const langPrefix = `/${i18n.language}`;
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [isConnectionConstrained, setIsConnectionConstrained] = useState(false);

  // Get media URLs from CMS or use defaults
  const videoUrl = getMedia('hero.backgroundVideoUrl', DEFAULT_VIDEO_URL);
  const posterUrl = getMedia('hero.posterImageUrl', DEFAULT_POSTER_URL);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setIsReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined") {
      return undefined;
    }
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (!connection) {
      return undefined;
    }

    const updateConnection = () => {
      const shouldDefer =
        Boolean(connection.saveData) ||
        ["slow-2g", "2g"].includes(connection.effectiveType || "");
      setIsConnectionConstrained(shouldDefer);
    };

    updateConnection();

    if (typeof connection.addEventListener === "function") {
      connection.addEventListener("change", updateConnection);
      return () =>
        connection.removeEventListener("change", updateConnection);
    }

    if (typeof connection.addListener === "function") {
      connection.addListener(updateConnection);
      return () => connection.removeListener(updateConnection);
    }

    return undefined;
  }, []);

  const shouldPlayVideo = !(isReducedMotion || isConnectionConstrained);

  useEffect(() => {
    if (!shouldPlayVideo) {
      setVideoLoaded(true);
    }
  }, [shouldPlayVideo]);

  // Animation hooks
  const [heroRef, isHeroVisible] = useScrollAnimation(0.2);
  const [parallaxRef, parallaxOffset] = useParallax(0.3);

  return (
    <section data-hero="true" data-cms-section="hero" data-cms-page="home"
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Video/Image - Enhanced with parallax */}
      <div
        ref={parallaxRef}
        className="absolute inset-0 overflow-hidden"
        style={{ transform: `translateY(${parallaxOffset}px)` }}
      >
        {/* Loading indicator */}
        {!videoLoaded && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: colors.gradients.heroSolid }}>
            <div className="text-center" style={{ color: '#EAF2FA' }}>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#3F7BA7' }}></div>
              <p className="text-lg">Loading magical experience...</p>
            </div>
          </div>
        )}

        {shouldPlayVideo ? (
          <video
            className="absolute inset-0 w-full h-full object-cover scale-110" // Ensure full coverage
            src={videoUrl}
            autoPlay
            loop
            muted
            playsInline
            preload={isMobile ? "none" : "metadata"} // Reduce mobile data usage
            poster={posterUrl}
            onLoadedData={() => setVideoLoaded(true)}
            onCanPlay={() => setVideoLoaded(true)}
            onError={() => {
              setVideoLoaded(true); // Show content even if video fails
            }}
            // Ensure video loops properly on all devices
            onEnded={(e) => {
              e.target.currentTime = 0;
              e.target.play();
            }}
            // Additional mobile optimizations
            style={{
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              // Ensure video covers full area on mobile
              minWidth: '100%',
              minHeight: '100%'
            }}
          />
        ) : (
          <img
            src={posterUrl}
            alt={t("hero.videoFallbackAlt", "Lapland winter landscape under the northern lights")}
            className="absolute inset-0 w-full h-full object-cover scale-110"
            style={{
              willChange: "transform",
              backfaceVisibility: "hidden",
              minWidth: "100%",
              minHeight: "100%",
            }}
          />
        )}
        {/* Custom brand gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: colors.gradients.hero,
          }}
        ></div>
      </div>      {/* Floating Elements - Enhanced with animations */}
      <div className="absolute inset-0 pointer-events-none">
        <Snowflake
          className={`absolute top-20 left-10 h-6 w-6 transition-all duration-1000 ${isHeroVisible ? 'animate-pulse opacity-100' : 'opacity-0'
            }`}
          style={{ color: '#C6D4E4', opacity: 0.3 }}
        />
        <Snowflake
          className={`absolute top-40 right-20 h-4 w-4 transition-all duration-1000 delay-200 ${isHeroVisible ? 'animate-bounce opacity-100' : 'opacity-0'
            }`}
          style={{ color: '#EAF2FA', opacity: 0.2 }}
        />
        <Snowflake
          className={`absolute bottom-40 left-20 h-8 w-8 transition-all duration-1000 delay-400 ${isHeroVisible ? 'animate-pulse opacity-100' : 'opacity-0'
            }`}
          style={{ color: '#C6D4E4', opacity: 0.25 }}
        />
        <Snowflake
          className={`absolute bottom-20 right-10 h-5 w-5 transition-all duration-1000 delay-600 ${isHeroVisible ? 'animate-bounce opacity-100' : 'opacity-0'
            }`}
          style={{ color: '#EAF2FA', opacity: 0.3 }}
        />
      </div>

      {/* Content - Enhanced with stagger animations */}
      <div className={`relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto transition-all duration-1000 ${isHeroVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
        {/* Language Switcher removed, now in Header */}

        {/* Enhanced title */}
        <HeroHeading
          primary={t('hero.title')}
          accent={t('hero.magic')}
          stacked
          textAlign="center"
          className={`mb-6 leading-[1.15] transition-all duration-1000 delay-400 ${isHeroVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            } ${isMobile ? 'text-4xl' : isTablet ? 'text-5xl' : 'text-5xl md:text-7xl'
            }`}
        />

        <p className={`mb-8 max-w-3xl mx-auto leading-relaxed transition-all duration-1000 delay-600 ${isHeroVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          } ${isMobile ? 'text-lg' : 'text-xl md:text-2xl'
          }`} style={{ color: '#C6D4E4' }}>
          {t('hero.subtitle')}
        </p>

        <div className={`flex gap-4 justify-center items-center mb-12 transition-all duration-1000 delay-800 ${isHeroVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          } ${isMobile ? 'flex-col' : 'flex-col sm:flex-row'
          }`}>
          {/* Primary CTA Button */}
          <PrimaryButton
            to={`${langPrefix}/book`}
            size="large"
            className="w-full sm:w-auto"
          >
            {t('hero.cta')}
          </PrimaryButton>

          {/* Secondary CTA Button */}
          <OutlineButton
            to={`${langPrefix}/packages`}
            size="large"
            className="w-full sm:w-auto"
          >
            {t('hero.explore')}
          </OutlineButton>
        </div>
        {/* Features Preview - Hidden on mobile */}
        {!isMobile && (
          <div className="gap-6 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3">
            <InteractiveCard
              variant="glass"
              className="relative overflow-hidden"
              style={{ background: 'rgba(34, 59, 86, 0.3)', backdropFilter: 'blur(0.5px)', border: `1px solid rgba(234, 242, 250, 0.1)` }}
              contentClassName="p-6 relative z-10"
            >
              {/* Mycket subtil frost effect */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(234, 242, 250, 0.02) 0%, rgba(63, 123, 167, 0.05) 100%)' }}></div>
              <div className="flex items-center mb-3">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg mr-3" style={{ backgroundColor: 'rgba(63, 123, 167, 0.2)', border: `1px solid rgba(198, 212, 228, 0.2)` }}>
                  <Snowflake size={18} style={{ color: '#C6D4E4' }} />
                </div>
                <h3 className="font-semibold text-lg" style={{ color: '#EAF2FA' }}>{t('hero.feature1Title')}</h3>
              </div>
              <p style={{ color: '#C6D4E4' }}>{t('hero.feature1Desc')}</p>
            </InteractiveCard>

            <InteractiveCard
              variant="glass"
              className="relative overflow-hidden"
              style={{ background: 'rgba(34, 59, 86, 0.3)', backdropFilter: 'blur(0.5px)', border: `1px solid rgba(234, 242, 250, 0.1)` }}
              contentClassName="p-6 relative z-10"
            >
              {/* Mycket subtil frost effect */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(234, 242, 250, 0.02) 0%, rgba(63, 123, 167, 0.05) 100%)' }}></div>
              <div className="flex items-center mb-3">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg mr-3" style={{ backgroundColor: 'rgba(63, 123, 167, 0.2)', border: `1px solid rgba(198, 212, 228, 0.2)` }}>
                  <Star size={18} style={{ color: '#C6D4E4' }} />
                </div>
                <h3 className="font-semibold text-lg" style={{ color: '#EAF2FA' }}>{t('hero.feature2Title')}</h3>
              </div>
              <p style={{ color: '#C6D4E4' }}>{t('hero.feature2Desc')}</p>
            </InteractiveCard>

            <InteractiveCard
              variant="glass"
              className="relative overflow-hidden"
              style={{ background: 'rgba(34, 59, 86, 0.3)', backdropFilter: 'blur(0.5px)', border: `1px solid rgba(234, 242, 250, 0.1)` }}
              contentClassName="p-6 relative z-10"
            >
              {/* Mycket subtil frost effect */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(234, 242, 250, 0.02) 0%, rgba(63, 123, 167, 0.05) 100%)' }}></div>
              <div className="flex items-center mb-3">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg mr-3" style={{ backgroundColor: 'rgba(63, 123, 167, 0.2)', border: `1px solid rgba(198, 212, 228, 0.2)` }}>
                  <ArrowRight size={18} style={{ color: '#C6D4E4' }} />
                </div>
                <h3 className="font-semibold text-lg" style={{ color: '#EAF2FA' }}>{t('hero.feature3Title')}</h3>
              </div>
              <p style={{ color: '#C6D4E4' }}>{t('hero.feature3Desc')}</p>
            </InteractiveCard>
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
